import { Chip, TableCell, Typography } from '@mui/material';
import { FormattedTime } from '@/components/ui/formatted-time';
import { DATETIME_WITH_SECONDS } from '@/lib/timezone';
import { colors, typography as t, radius } from '@/theme/tokens';

/** Shared vertical rhythm for software-update event rows. */
export const EVENT_ROW_SX = { '& td': { py: 1.8 } } as const;

/** Monospace timestamp cell, kept on a single line. */
export function EventTimeCell({ value }: { value?: string | null }) {
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      {value ? (
        <FormattedTime
          value={value}
          variant="absolute"
          timeOptions={DATETIME_WITH_SECONDS}
          color="text.secondary"
          sx={{ fontFamily: 'monospace', fontSize: 11 }}
        />
      ) : (
        <Dash />
      )}
    </TableCell>
  );
}

/** Compact status pill used for event types and reasons. */
export function EventChip({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: t.fontWeight.medium,
        borderRadius: `${radius.sm}px`,
        bgcolor: bg,
        color,
      }}
    />
  );
}

/** Neutral grey variant of {@link EventChip} for reason codes. */
export function ReasonChip({ label }: { label: string }) {
  return <EventChip label={label} bg={colors.grey[50]} color={colors.text.secondary} />;
}

/**
 * "From version" cell for device update events: the installed version snapshotted at
 * event time. `null` means no prior version was captured (rendered as an em dash)
 * and is never derived from current device state.
 */
export function EventFromVersionCell({ fromVersion }: { fromVersion: string | null }) {
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      {fromVersion ? (
        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
          {fromVersion}
        </Typography>
      ) : (
        <Dash />
      )}
    </TableCell>
  );
}

/** "To version" cell for device update events: the event's target version. */
export function EventToVersionCell({ targetVersion }: { targetVersion: string }) {
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      {targetVersion ? (
        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
          {targetVersion}
        </Typography>
      ) : (
        <Dash />
      )}
    </TableCell>
  );
}

/** Muted em dash for empty cells. */
export function Dash() {
  return (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
}
